import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDisclosure } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { useHtmlFormInputs } from '../../hooks/useHtmlFormInputs/useHtmlFormInputs';
import { CategoryBillingQuestion } from '../../../api/services/categories';
import { useCategoryDealNameTemplates } from '../../../api/hooks/useCategoryDealNameTemplates';
import { useCategoryBillingQuestions } from '../../../api/hooks/useCategoryBillingQuestions';

export type TemplateOption = {
    label: string;
    value: number;
};

export type UseProvideDetailsProps = {
    inputs: ReturnType<typeof useHtmlFormInputs>;
};

export function useProvideDetails({ inputs }: UseProvideDetailsProps) {
    const [, { language }] = useTranslation();
    const { watch, setValue, clearErrors } = useFormContext();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [billingQuestion, setBillingQuestion] = useState<CategoryBillingQuestion | undefined>();
    const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);

    const subcategory: number | null = watch('subcategory');
    const billingAnswer: string | null = watch('billing_answer');

    const { mutate: getDealNameTemplates, isSuccess: isLoadedTemplates } =
        useCategoryDealNameTemplates({
            onSuccess: (data) => {
                const options = convertIntoOptions(data);
                setTemplateOptions(options);

                const isNotSameOptions =
                    options.find(
                        (option) =>
                            String(inputs.deal_name_template.input.current.value) ===
                            String(option.value),
                    ) === undefined;
                const noOptions = options.length === 0;

                if (noOptions) {
                    setValue('deal_name_template', null);
                    inputs.deal_name_template.onChange(null);
                }

                if (!noOptions && isNotSameOptions) {
                    setValue('deal_name_template', null);
                    inputs.deal_name_template.onChange(null);
                }
            },
        });

    const { mutate: getQuestions, isSuccess: isLoadedQuestions } = useCategoryBillingQuestions({
        onSuccess: (data) => {
            if (data.length > 0) {
                setBillingQuestion(data[0]);
            } else {
                setBillingQuestion(undefined);
                setValue('billing_answer', null);
                inputs.billing_answer.onChange(null);
            }
        },
    });

    useEffect(() => {
        if (language === 'pl' && subcategory !== null) {
            getQuestions({ categoryId: subcategory });
            getDealNameTemplates({ categoryId: subcategory });
        } else {
            setBillingQuestion(undefined);
            setTemplateOptions([]);
        }
    }, [subcategory]);

    useEffect(() => {
        if (language === 'pl') {
            const areNoQuestions = billingQuestion === undefined;
            const areQuestionsWithNoAnswer =
                billingQuestion !== undefined && billingAnswer !== null && billingAnswer !== '';
            if (subcategory !== null && (areNoQuestions || areQuestionsWithNoAnswer)) {
                clearErrors(['name', 'deal_name_template', 'description']);
                onOpen();
            } else {
                onClose();
            }
        }
    }, [billingQuestion, subcategory, billingAnswer]);

    return {
        isOpen: (isOpen && isLoadedQuestions && isLoadedTemplates) || language !== 'pl',
        billingQuestion,
        templateOptions,
    };
}

function convertIntoOptions(data: any): TemplateOption[] {
    return data.map((item: any) => ({
        label: item.deal_name,
        value: item.id,
    }));
}
